import { rectangleRing } from './defaults';
import { estimateTextBoxLayout } from './textLayout';
import type { Polygon, MultiPolygon } from 'geojson';
import type {
  Annotation,
  AnnotationAreaPart,
  AnnotationCoordinate,
  AnnotationEndpointStyle,
  AnnotationMovePart,
} from './model';

export function annotationAreaParts(
  annotation: Extract<Annotation, { type: 'polygon' | 'focus-area' }>,
): readonly AnnotationAreaPart[] {
  return [
    {
      coordinates: annotation.coordinates,
      ...(annotation.holes ? { holes: annotation.holes } : {}),
    },
    ...(annotation.additionalParts ?? []),
  ];
}

export function annotationAreaGeometry(
  annotation: Extract<Annotation, { type: 'polygon' | 'focus-area' }>,
): Polygon | MultiPolygon {
  const parts = annotationAreaParts(annotation).map((part) =>
    [part.coordinates, ...(part.holes ?? [])].map((ring) => ring.map((point) => [...point])),
  );
  return parts.length === 1
    ? { type: 'Polygon', coordinates: parts[0]! }
    : { type: 'MultiPolygon', coordinates: parts };
}

export function annotationAreaRings(
  annotation: Extract<Annotation, { type: 'polygon' | 'focus-area' }>,
): readonly (readonly AnnotationCoordinate[])[] {
  return annotationAreaParts(annotation).flatMap((part) => [
    part.coordinates,
    ...(part.holes ?? []),
  ]);
}

export function annotationCoordinates(annotation: Annotation): readonly AnnotationCoordinate[] {
  switch (annotation.type) {
    case 'marker':
    case 'text':
      return [annotation.position];
    case 'callout':
      return [annotation.target, annotation.textPosition];
    case 'line':
    case 'rectangle':
      return annotation.coordinates;
    case 'polygon':
    case 'focus-area':
      return annotationAreaRings(annotation).flat();
    case 'ellipse':
      return annotation.bounds;
  }
}

export function translateAnnotation(
  annotation: Annotation,
  longitudeDelta: number,
  latitudeDelta: number,
): Annotation {
  const move = ([longitude, latitude]: AnnotationCoordinate): AnnotationCoordinate => [
    Math.max(-180, Math.min(180, longitude + longitudeDelta)),
    Math.max(-85.051129, Math.min(85.051129, latitude + latitudeDelta)),
  ];
  switch (annotation.type) {
    case 'marker':
    case 'text':
      return { ...annotation, position: move(annotation.position) };
    case 'callout':
      return {
        ...annotation,
        target: move(annotation.target),
        textPosition: move(annotation.textPosition),
      };
    case 'line':
    case 'rectangle':
      return { ...annotation, coordinates: annotation.coordinates.map(move) };
    case 'polygon':
    case 'focus-area':
      return {
        ...annotation,
        coordinates: annotation.coordinates.map(move),
        ...(annotation.holes ? { holes: annotation.holes.map((ring) => ring.map(move)) } : {}),
        ...(annotation.additionalParts
          ? {
              additionalParts: annotation.additionalParts.map((part) => ({
                coordinates: part.coordinates.map(move),
                ...(part.holes ? { holes: part.holes.map((ring) => ring.map(move)) } : {}),
              })),
            }
          : {}),
      };
    case 'ellipse':
      return { ...annotation, bounds: [move(annotation.bounds[0]), move(annotation.bounds[1])] };
  }
}

const moveCoordinate = (
  [longitude, latitude]: AnnotationCoordinate,
  longitudeDelta: number,
  latitudeDelta: number,
): AnnotationCoordinate => [
  Math.max(-180, Math.min(180, longitude + longitudeDelta)),
  Math.max(-85.051129, Math.min(85.051129, latitude + latitudeDelta)),
];

/** Pure interaction transform shared by transient previews and the durable pointer-up commit. */
export function transformAnnotationForDrag(
  annotation: Annotation,
  part: AnnotationMovePart,
  longitudeDelta: number,
  latitudeDelta: number,
  widthDelta = 0,
  heightDelta = 0,
): Annotation {
  if (
    (annotation.type === 'polygon' || annotation.type === 'focus-area') &&
    annotation.additionalParts?.length &&
    (part.startsWith('vertex-') || part.startsWith('midpoint-'))
  )
    return annotation;
  if (
    (annotation.type === 'text' || annotation.type === 'callout') &&
    (part === 'text-width' || part === 'text-height' || part === 'text-size')
  ) {
    const boxWidthDelta =
      annotation.style.alignment === 'right'
        ? -widthDelta
        : annotation.style.alignment === 'centre'
          ? widthDelta * 2
          : widthDelta;
    return {
      ...annotation,
      style: {
        ...annotation.style,
        ...(part !== 'text-height'
          ? { boxWidth: Math.max(40, Math.min(800, annotation.style.boxWidth + boxWidthDelta)) }
          : {}),
        ...(part !== 'text-width'
          ? {
              boxHeight: Math.max(
                8,
                Math.min(
                  1200,
                  estimateTextBoxLayout(annotation.text, annotation.style).contentHeight +
                    heightDelta * 2,
                ),
              ),
            }
          : {}),
      },
    } as typeof annotation;
  }
  if (annotation.type === 'rectangle' && part.startsWith('rectangle-corner-')) {
    const cornerIndex = Number(part.at(-1)) as 0 | 1 | 2 | 3;
    const oppositeIndex = ((cornerIndex + 2) % 4) as 0 | 1 | 2 | 3;
    if (annotation.rotationDegrees !== 0) {
      const handles = rectangleRenderRing(annotation.coordinates, annotation.rotationDegrees);
      const moved = moveCoordinate(handles[cornerIndex]!, longitudeDelta, latitudeDelta);
      return {
        ...annotation,
        coordinates: resizeRotatedRectangle(
          annotation.coordinates,
          cornerIndex,
          moved,
          annotation.rotationDegrees,
        ),
      };
    }
    return {
      ...annotation,
      coordinates: rectangleRing(
        moveCoordinate(annotation.coordinates[cornerIndex]!, longitudeDelta, latitudeDelta),
        annotation.coordinates[oppositeIndex]!,
      ),
    };
  }
  if (annotation.type === 'ellipse' && part.startsWith('ellipse-handle-')) {
    const handles = ellipseHandles(
      annotation.bounds,
      annotation.circleLocked,
      annotation.rotationDegrees,
    );
    const moved = moveCoordinate(
      handles[part.slice('ellipse-handle-'.length) as keyof typeof handles],
      longitudeDelta,
      latitudeDelta,
    );
    return {
      ...annotation,
      bounds: ellipseBoundsFromHandle(
        annotation.bounds,
        part.slice('ellipse-handle-'.length) as keyof typeof handles,
        moved,
        annotation.circleLocked,
        annotation.rotationDegrees,
      ),
    };
  }
  if (
    (annotation.type === 'polygon' || annotation.type === 'focus-area') &&
    part.startsWith('midpoint-')
  ) {
    const insertIndex = Number(part.slice('midpoint-'.length));
    const vertices = annotation.coordinates.slice(0, -1);
    if (!Number.isInteger(insertIndex) || insertIndex < 1 || insertIndex > vertices.length)
      return annotation;
    const midpoint = polygonMidpoints(annotation.coordinates).find(
      (candidate) => candidate.insertIndex === insertIndex,
    )?.coordinate;
    if (!midpoint) return annotation;
    const inserted = moveCoordinate(midpoint, longitudeDelta, latitudeDelta);
    const next = [...vertices];
    next.splice(insertIndex, 0, inserted);
    return { ...annotation, coordinates: [...next, next[0]!] };
  }
  if (
    (annotation.type === 'line' ||
      annotation.type === 'polygon' ||
      annotation.type === 'focus-area') &&
    part.startsWith('vertex-')
  ) {
    const index = Number(part.slice('vertex-'.length));
    if (!Number.isInteger(index) || index < 0 || index >= annotation.coordinates.length)
      return annotation;
    const coordinates = [...annotation.coordinates];
    coordinates[index] = moveCoordinate(coordinates[index]!, longitudeDelta, latitudeDelta);
    if (annotation.type !== 'line' && index === 0)
      coordinates[coordinates.length - 1] = coordinates[0]!;
    return { ...annotation, coordinates };
  }
  if (annotation.type === 'callout' && part !== 'object') {
    return part === 'target'
      ? {
          ...annotation,
          target: moveCoordinate(annotation.target, longitudeDelta, latitudeDelta),
        }
      : {
          ...annotation,
          textPosition: moveCoordinate(annotation.textPosition, longitudeDelta, latitudeDelta),
        };
  }
  return translateAnnotation(annotation, longitudeDelta, latitudeDelta);
}

export interface PlanarPoint {
  readonly x: number;
  readonly y: number;
}

const mercatorPoint = ([longitude, latitude]: AnnotationCoordinate): PlanarPoint => {
  const latitudeRadians = (latitude * Math.PI) / 180;
  return {
    x: (longitude + 180) / 360,
    y: (1 - Math.asinh(Math.tan(latitudeRadians)) / Math.PI) / 2,
  };
};

const mercatorCoordinate = ({ x, y }: PlanarPoint): AnnotationCoordinate => [
  x * 360 - 180,
  (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI,
];

export function normalizeRotationDegrees(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const normalised = value % 360;
  return normalised < 0 ? normalised + 360 : normalised;
}

function rotatePoint(point: PlanarPoint, centre: PlanarPoint, degrees: number): PlanarPoint {
  const angle = (normalizeRotationDegrees(degrees) * Math.PI) / 180;
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  const x = point.x - centre.x;
  const y = point.y - centre.y;
  return {
    x: centre.x + x * cosine - y * sine,
    y: centre.y + x * sine + y * cosine,
  };
}

export function rectangleRenderRing(
  coordinates: readonly AnnotationCoordinate[],
  rotationDegrees: number,
): readonly AnnotationCoordinate[] {
  if (normalizeRotationDegrees(rotationDegrees) === 0) return coordinates;
  const points = coordinates.slice(0, 4).map(mercatorPoint);
  const first = points[0]!;
  const opposite = points[2]!;
  const centre = { x: (first.x + opposite.x) / 2, y: (first.y + opposite.y) / 2 };
  const rotated = points.map((point) =>
    mercatorCoordinate(rotatePoint(point, centre, rotationDegrees)),
  );
  return [...rotated, rotated[0]!];
}

/** Derive a display-space rounded rectangle while leaving its canonical authored extent untouched. */
export function roundedRectanglePoints(
  ring: readonly PlanarPoint[],
  radius: number,
  segmentsPerCorner?: number,
): readonly PlanarPoint[] {
  if (radius <= 0 || ring.length < 4) return ring;
  const corners = ring.slice(0, 4);
  const edgeLengths = corners.map((corner, index) => {
    const next = corners[(index + 1) % corners.length]!;
    return Math.hypot(next.x - corner!.x, next.y - corner!.y);
  });
  const effectiveRadius = Math.min(radius, Math.min(...edgeLengths) / 2);
  if (!(effectiveRadius > 0)) return ring;
  // Approximate each quarter-circle below a small screen-space chord error. The
  // bounds keep annotation GeoJSON compact even for unusually large authored radii.
  const arcSegments =
    segmentsPerCorner ??
    Math.max(
      16,
      Math.min(
        48,
        Math.ceil(Math.PI / 2 / Math.acos(Math.max(-1, Math.min(1, 1 - 0.075 / effectiveRadius)))),
      ),
    );
  // Standard cubic approximation for a quarter-circle. Unlike the previous
  // corner-controlled quadratic, its curvature and tangents closely follow the intended arc.
  const kappa = 0.552284749831;
  const points: PlanarPoint[] = [];
  for (let index = 0; index < corners.length; index += 1) {
    const corner = corners[index]!;
    const previous = corners[(index + corners.length - 1) % corners.length]!;
    const next = corners[(index + 1) % corners.length]!;
    const previousLength = Math.hypot(previous.x - corner.x, previous.y - corner.y);
    const nextLength = Math.hypot(next.x - corner.x, next.y - corner.y);
    const entry = {
      x: corner.x + ((previous.x - corner.x) / previousLength) * effectiveRadius,
      y: corner.y + ((previous.y - corner.y) / previousLength) * effectiveRadius,
    };
    const exit = {
      x: corner.x + ((next.x - corner.x) / nextLength) * effectiveRadius,
      y: corner.y + ((next.y - corner.y) / nextLength) * effectiveRadius,
    };
    const entryControl = {
      x: entry.x + (corner.x - entry.x) * kappa,
      y: entry.y + (corner.y - entry.y) * kappa,
    };
    const exitControl = {
      x: exit.x + (corner.x - exit.x) * kappa,
      y: exit.y + (corner.y - exit.y) * kappa,
    };
    for (let step = 0; step <= arcSegments; step += 1) {
      const t = step / arcSegments;
      const inverse = 1 - t;
      points.push({
        x:
          inverse ** 3 * entry.x +
          3 * inverse ** 2 * t * entryControl.x +
          3 * inverse * t ** 2 * exitControl.x +
          t ** 3 * exit.x,
        y:
          inverse ** 3 * entry.y +
          3 * inverse ** 2 * t * entryControl.y +
          3 * inverse * t ** 2 * exitControl.y +
          t ** 3 * exit.y,
      });
    }
  }
  return [...points, points[0]!];
}

function resizeRotatedRectangle(
  coordinates: readonly AnnotationCoordinate[],
  cornerIndex: 0 | 1 | 2 | 3,
  movedCoordinate: AnnotationCoordinate,
  rotationDegrees: number,
): readonly AnnotationCoordinate[] {
  const first = mercatorPoint(coordinates[0]!);
  const opposite = mercatorPoint(coordinates[2]!);
  const centre = { x: (first.x + opposite.x) / 2, y: (first.y + opposite.y) / 2 };
  const local = rotatePoint(mercatorPoint(movedCoordinate), centre, -rotationDegrees);
  const halfWidth = Math.max(1e-10, Math.abs(local.x - centre.x));
  const halfHeight = Math.max(1e-10, Math.abs(local.y - centre.y));
  const xSign = cornerIndex === 0 || cornerIndex === 3 ? -1 : 1;
  const ySign = cornerIndex === 0 || cornerIndex === 1 ? -1 : 1;
  const corner = mercatorCoordinate({
    x: centre.x + xSign * halfWidth,
    y: centre.y + ySign * halfHeight,
  });
  const oppositeCorner = mercatorCoordinate({
    x: centre.x - xSign * halfWidth,
    y: centre.y - ySign * halfHeight,
  });
  return rectangleRing(corner, oppositeCorner);
}

export function polygonMidpoints(
  ring: readonly AnnotationCoordinate[],
): readonly { readonly coordinate: AnnotationCoordinate; readonly insertIndex: number }[] {
  const vertices = ring.slice(0, -1);
  return vertices.map((coordinate, index) => {
    const next = vertices[(index + 1) % vertices.length]!;
    const start = mercatorPoint(coordinate);
    const end = mercatorPoint(next);
    return {
      coordinate: mercatorCoordinate({ x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 }),
      insertIndex: index + 1,
    };
  });
}

export function constrainedEllipseBounds(
  bounds: readonly [AnnotationCoordinate, AnnotationCoordinate],
  circleLocked: boolean,
): readonly [AnnotationCoordinate, AnnotationCoordinate] {
  if (!circleLocked) return bounds;
  const first = mercatorPoint(bounds[0]);
  const second = mercatorPoint(bounds[1]);
  const centre = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
  const radius = Math.max(Math.abs(second.x - first.x), Math.abs(second.y - first.y)) / 2;
  return [
    mercatorCoordinate({ x: centre.x - radius, y: centre.y - radius }),
    mercatorCoordinate({ x: centre.x + radius, y: centre.y + radius }),
  ];
}

export function ellipseRing(
  bounds: readonly [AnnotationCoordinate, AnnotationCoordinate],
  circleLocked: boolean,
  rotationDegrees = 0,
  segments = 64,
): readonly AnnotationCoordinate[] {
  const [a, b] = constrainedEllipseBounds(bounds, circleLocked).map(mercatorPoint);
  const centre = { x: (a!.x + b!.x) / 2, y: (a!.y + b!.y) / 2 };
  const radiusX = Math.abs(b!.x - a!.x) / 2;
  const radiusY = Math.abs(b!.y - a!.y) / 2;
  const count = Math.max(24, Math.min(128, Math.round(segments)));
  return Array.from({ length: count + 1 }, (_, index) => {
    const angle = (index / count) * Math.PI * 2;
    return mercatorCoordinate(
      rotatePoint(
        {
          x: centre.x + Math.cos(angle) * radiusX,
          y: centre.y + Math.sin(angle) * radiusY,
        },
        centre,
        rotationDegrees,
      ),
    );
  });
}

export function ellipseHandles(
  bounds: readonly [AnnotationCoordinate, AnnotationCoordinate],
  circleLocked: boolean,
  rotationDegrees = 0,
): Readonly<Record<'left' | 'right' | 'top' | 'bottom', AnnotationCoordinate>> {
  const [a, b] = constrainedEllipseBounds(bounds, circleLocked).map(mercatorPoint);
  const left = Math.min(a!.x, b!.x);
  const right = Math.max(a!.x, b!.x);
  const top = Math.min(a!.y, b!.y);
  const bottom = Math.max(a!.y, b!.y);
  const centre = { x: (left + right) / 2, y: (top + bottom) / 2 };
  return {
    left: mercatorCoordinate(rotatePoint({ x: left, y: centre.y }, centre, rotationDegrees)),
    right: mercatorCoordinate(rotatePoint({ x: right, y: centre.y }, centre, rotationDegrees)),
    top: mercatorCoordinate(rotatePoint({ x: centre.x, y: top }, centre, rotationDegrees)),
    bottom: mercatorCoordinate(rotatePoint({ x: centre.x, y: bottom }, centre, rotationDegrees)),
  };
}

function ellipseBoundsFromHandle(
  bounds: readonly [AnnotationCoordinate, AnnotationCoordinate],
  handle: 'left' | 'right' | 'top' | 'bottom',
  coordinate: AnnotationCoordinate,
  circleLocked: boolean,
  rotationDegrees: number,
): readonly [AnnotationCoordinate, AnnotationCoordinate] {
  const [a, b] = constrainedEllipseBounds(bounds, circleLocked).map(mercatorPoint);
  const centre = { x: (a!.x + b!.x) / 2, y: (a!.y + b!.y) / 2 };
  const moved = rotatePoint(mercatorPoint(coordinate), centre, -rotationDegrees);
  let radiusX = Math.max(1e-10, Math.abs(b!.x - a!.x) / 2);
  let radiusY = Math.max(1e-10, Math.abs(b!.y - a!.y) / 2);
  if (handle === 'left' || handle === 'right')
    radiusX = Math.max(1e-10, Math.abs(moved.x - centre.x));
  else radiusY = Math.max(1e-10, Math.abs(moved.y - centre.y));
  if (circleLocked) radiusX = radiusY = handle === 'left' || handle === 'right' ? radiusX : radiusY;
  return [
    mercatorCoordinate({ x: centre.x - radiusX, y: centre.y - radiusY }),
    mercatorCoordinate({ x: centre.x + radiusX, y: centre.y + radiusY }),
  ];
}

export interface ArrowGeometry {
  readonly direction: PlanarPoint;
  readonly tip: PlanarPoint;
  readonly baseCentre: PlanarPoint;
  readonly wings: readonly [PlanarPoint, PlanarPoint];
  readonly head: readonly [PlanarPoint, PlanarPoint, PlanarPoint];
  readonly shaft: readonly PlanarPoint[];
}

export interface EndpointMarkerGeometry {
  readonly shaft: readonly PlanarPoint[];
  readonly marker:
    | { readonly kind: 'polygon'; readonly points: readonly PlanarPoint[] }
    | { readonly kind: 'circle'; readonly centre: PlanarPoint; readonly radius: number };
}

const squaredDistance = (a: PlanarPoint, b: PlanarPoint) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;

/** Return the final rendered segment, skipping only consecutive coincident points at its tip. */
export function lastDistinctSegment(
  points: readonly PlanarPoint[],
  tolerance = 0.01,
): readonly [PlanarPoint, PlanarPoint] | null {
  const tip = points.at(-1);
  if (!tip) return null;
  const toleranceSquared = tolerance ** 2;
  for (let index = points.length - 2; index >= 0; index -= 1) {
    const previous = points[index]!;
    if (squaredDistance(previous, tip) > toleranceSquared) return [previous, tip];
  }
  return null;
}

/** Collapse consecutive geographic duplicates without removing legitimate repeated vertices. */
export function appendDistinctCoordinate(
  coordinates: readonly AnnotationCoordinate[],
  coordinate: AnnotationCoordinate,
  tolerance = 1e-10,
): readonly AnnotationCoordinate[] {
  const previous = coordinates.at(-1);
  if (
    previous &&
    Math.abs(previous[0] - coordinate[0]) <= tolerance &&
    Math.abs(previous[1] - coordinate[1]) <= tolerance
  )
    return coordinates;
  return [...coordinates, coordinate];
}

/** Shared triangular arrowhead geometry. Size is in the caller's physical render units. */
export function arrowheadPoints(
  previous: PlanarPoint,
  tip: PlanarPoint,
  size: number,
): readonly PlanarPoint[] {
  const angle = Math.atan2(tip.y - previous.y, tip.x - previous.x);
  const back = size;
  const half = size * 0.52;
  const base = { x: tip.x - Math.cos(angle) * back, y: tip.y - Math.sin(angle) * back };
  return [
    tip,
    {
      x: base.x + Math.cos(angle + Math.PI / 2) * half,
      y: base.y + Math.sin(angle + Math.PI / 2) * half,
    },
    {
      x: base.x + Math.cos(angle - Math.PI / 2) * half,
      y: base.y + Math.sin(angle - Math.PI / 2) * half,
    },
  ];
}

/**
 * Derive one render-unit arrow model for map and export renderers. The shaft is shortened to the
 * arrowhead base with a sub-pixel overlap that closes antialiasing seams without reaching the tip.
 */
export function arrowGeometry(
  points: readonly PlanarPoint[],
  lineWidth: number,
  headLength: number,
  tolerance = 0.01,
): ArrowGeometry | null {
  const segment = lastDistinctSegment(points, tolerance);
  if (!segment) return null;
  const [previous, tip] = segment;
  const segmentLength = Math.hypot(tip.x - previous.x, tip.y - previous.y);
  const direction = {
    x: (tip.x - previous.x) / segmentLength,
    y: (tip.y - previous.y) / segmentLength,
  };
  const baseCentre = {
    x: tip.x - direction.x * headLength,
    y: tip.y - direction.y * headLength,
  };
  const halfWidth = headLength * 0.52;
  const normal = { x: -direction.y, y: direction.x };
  const wings = [
    { x: baseCentre.x + normal.x * halfWidth, y: baseCentre.y + normal.y * halfWidth },
    { x: baseCentre.x - normal.x * halfWidth, y: baseCentre.y - normal.y * halfWidth },
  ] as const;
  const overlap = Math.min(0.75, Math.max(0.2, lineWidth * 0.15));
  const trimDistance = Math.min(segmentLength, Math.max(0, headLength - overlap));
  const trimmedTip = {
    x: tip.x - direction.x * trimDistance,
    y: tip.y - direction.y * trimDistance,
  };
  const toleranceSquared = tolerance ** 2;
  return {
    direction,
    tip,
    baseCentre,
    wings,
    head: [tip, wings[0], wings[1]],
    shaft: points.map((point, index) =>
      index > 0 && squaredDistance(point, tip) <= toleranceSquared ? trimmedTip : point,
    ),
  };
}

/** Shared Line/Callout endpoint geometry in the caller's physical render units. */
export function endpointMarkerGeometry(
  points: readonly PlanarPoint[],
  style: AnnotationEndpointStyle,
  lineWidth: number,
  size: number,
  tolerance = 0.01,
): EndpointMarkerGeometry | null {
  if (style === 'none') return null;
  const segment = lastDistinctSegment(points, tolerance);
  if (!segment) return null;
  const tip = segment[1];
  if (style === 'arrow') {
    const arrow = arrowGeometry(points, lineWidth, size, tolerance);
    return arrow ? { shaft: arrow.shaft, marker: { kind: 'polygon', points: arrow.head } } : null;
  }
  const radius = size / 2;
  if (style === 'filled-circle') {
    return { shaft: points, marker: { kind: 'circle', centre: tip, radius } };
  }
  return {
    shaft: points,
    marker: {
      kind: 'polygon',
      points: [
        { x: tip.x - radius, y: tip.y - radius },
        { x: tip.x + radius, y: tip.y - radius },
        { x: tip.x + radius, y: tip.y + radius },
        { x: tip.x - radius, y: tip.y + radius },
      ],
    },
  };
}
